<template>
  <div class="app-wrapper">
    <div v-if="device==='mobile' && sidebar.opened" class="drawer-bg" @click="handleClickOutside"></div>

    <!-- 1.左侧菜单栏 -->
    <ku-sidebar
      v-if="needSidebar"
      class-name="app-sidebar"
      :active-index="activeIndex"
      :menu-routes="menuRoutes"
      :unique-opened="uniqueOpened"
      :app-version="appVersion"
      :lang="lang"
      @on-sidebar-select="onSidebarSelect"
    />

    <!-- 2.右侧主体页面 -->
    <div class="main-container" :class="{'hideSidebar': !needSidebar}">
      <!-- 2.1顶部固定导航栏 -->
      <div :class="{'fixed-header': fixedHeader}">
        <!-- 2.1.1导航栏 -->
        <ku-navbar
          :show-home="needSidebar"
          :show-logo="showLogo"
          :nav-logo="iconMap['0']"
          :app-name="appName"
          :apis="navbarApis"
          :home-center="homeCenter"
          :logo-jump-url="logoJumpUrl"
          :exclude-menu="excludeNavMenu"
          :support-lang="!$route.query.lang && (supportLang || currentApp.code === 'home')"
          :lang="lang"
          :tenant-name="tenantName"
          :tenant-icon="iconMap['1']"
          :default-avatar="iconMap['2']"
          msg-center="/base-config/message/center"
          @on-change-lang="onChangeLang"
          @on-msg-refresh="onMsgUpdate"
        />

        <!-- 2.1.2顶部通知栏 -->
        <div v-if="swiperVisible" class="notify" :style="{ height: `${notifyHeight}px`, lineHeight: `${notifyHeight}px` }">
          <i class="el-icon-warning"></i>
          <div class="flex-1 overflow-hidden">
            <swiper ref="notifySwiper" class="notify-swiper-container swiper-no-swiping" :options="swiperOptions">
              <swiper-slide>{{ notify.content }}</swiper-slide>
            </swiper>
          </div>
          <i v-if="notify.closable" class="el-icon-close" @click="handleCloseNotify"></i>
        </div>

        <!-- 2.1.3消息通知弹窗 -->
        <ku-drawer
          size="1290px"
          :title="materialDrawer.title"
          :hide-confirm="true"
          :append-to-body="true"
          :modal-append-to-body="false"
          :visible.sync="materialDrawer.visible"
          @closed="handleMaterialDrawerClosed"
        >
          <MaterialListView :params="materialDrawer.params" />
        </ku-drawer>
      </div>

      <!-- 2.2页面主体容器 -->
      <section
        class="app-main"
        :class="{ 'hasNotify': notify.visible }"
        :style="{
          '--top': `${56 + notifyHeight}px`,
          paddingTop: `${56 + notifyHeight}px`
        }"
      >
        <transition name="fade-transform" mode="out-in">
          <keep-alive :include="cachedViews">
            <router-view v-if="isRouterViewShow" :key="routerKey" />
          </keep-alive>
        </transition>
      </section>
    </div>
  </div>
</template>

<script>
import db from '@klinx/utils/localstorage';
import resizeMixin from './mixin/resize-handler';
import websocketMixin from './mixin/websocket';
import appRouterMixin from './mixin/get-app-router';
import { getStringWidth } from '@klinx/utils/index';
import { baseAuthorityApi, baseMessageApi, baseTenantApi, baseApplicationApi, baseLangApi, baseCommonApi, baseFileApi } from '@klinx/base/src/api/index.js';
import { mapGetters } from 'vuex';
import { APP_CONFIG_MAP } from '../views/home/config';
import { axiosAllSettled } from '@klinx/utils';
import { LANGUAGE_MAP } from '@klinx/utils/lang-map';

// 一些默认图标
const DEFAULT_ICON_MAP = {
  '0': require('@klinx/components/packages/assets/iTEX-logo-40@2x.png'),
  '1': require('@klinx/components/packages/assets/tenant@3x.png'),
  '2': require('@klinx/components/packages/assets/avatar/default.png')
};

export default {
  name: 'Layout',
  mixins: [resizeMixin, websocketMixin, appRouterMixin],
  provide() {
    return {
      reloadRouter: this.reloadRouter
    };
  },
  data() {
    let lang = db.get('LANGUAGE', 'zh');
    // paas语言取cookie，saas本地化取db，语言切换统一都在登录页和门户也开放，应用内隐藏
    if (db.get('platform') === 'paas') {
      const paasLang = document.cookie.match(/lang=(\w+)/)?.[1]; // 形如zh_CN的格式
      Object.keys(LANGUAGE_MAP).forEach(key => {
        if (LANGUAGE_MAP[key] === paasLang) {
          lang = key;
        }
      });
    }

    return {
      lang,
      isRouterViewShow: false,
      activeIndex: '',
      navLogo: '',
      appName: this.$t('ITEX 工业云平台'), // todo: 获取应用名称
      appVersion: 'v3.0.0.0', // 应用版本号，前端本地写死
      homeCenter: '/home', // todo: 获取门户首页url
      isNavbarClicked: false,
      logoJumpUrl: '/home',
      excludeNavMenu: [],
      navbarApis: {
        login: baseAuthorityApi.login,
        getRouter: baseAuthorityApi.getRouter,
        getResource: baseAuthorityApi.getResource,
        logout: baseAuthorityApi.logout,
        getTips: baseMessageApi.getTips,
        mark: baseMessageApi.mark
      },
      swiperVisible: false,
      swiperOptions: null,
      received_messages: null,
      send_message: null,
      tenantName: '',
      supportLang: false, // 支持国际化切换
      menuBreadcrumb: [], // 菜单面包屑层级记录
      materialDrawer: {
        title: '',
        params: {},
        visible: false
      },
      iconMap: {
        '0': DEFAULT_ICON_MAP['0'],
        '1': DEFAULT_ICON_MAP['1'],
        '2': DEFAULT_ICON_MAP['2']
      }
    };
  },
  computed: {
    needSidebar() {
      if (!this.currentApp.code || this.currentApp.code === 'home') {
        return false;
      }

      if (!this.$route.meta?.onlyNavbar) {
        return true;
      }

      return false;
    },
    /**
     * 刚进页面时，需要拿后端visible的值
     *  刷新时，需要拿visible更新后的值
     *  关闭时，需要更新visible的值
     */
    notify() {
      return this.$store.state.account.notify;
    },
    notifyHeight() {
      return this.notify.visible ? 40 : 0;
    },
    sidebar() {
      return this.$store.state.setting.sidebar;
    },
    device() {
      return this.$store.state.setting.device;
    },
    needTagsView() {
      return this.$store.state.setting.multipage;
    },
    fixedHeader() {
      return this.$store.state.setting.fixHeader;
    },
    menuRoutes() {
      return this.$store.state.account.routes;
    },
    uniqueOpened() {
      return this.$store.state.setting.uniqueOpened;
    },
    showLogo() {
      return this.$store.state.setting.sidebarLogo;
    },
    cachedViews() {
      return this.$store.state.tagsView.cachedViews;
    },
    routerKey() {
      return this.$route.path;
    },
    tenant() {
      return this.$store.state.account.tenant;
    },
    ...mapGetters([
      'currentApp'
    ])
  },
  watch: {
    currentApp: {
      handler(app) {
        if (app.appName) {
          this.appName = app.appName;
          this.appVersion = app.version;
        }

        this.logoJumpUrl = app.code && app.code !== 'home' ? '/' : '/home';
        // Object.keys(this.deviceSwiper).length && this.deviceSwiper.onResize();
      },
      immediate: true
    },
    $route(route) {
      // 如果是点击切换，不用再次匹配index
      if (this.isNavbarClicked) {
        this.isNavbarClicked = false;
        return;
      }
      this.activeIndex = this.getMenuIndexByPath(route.path, route.query.url);
    },
    notify: {
      handler(val) {
        /**
         * 需要轮播：
         *  visible为true && 文字超出当前通知栏宽度
         */
        if (!val.visible) {
          this.$refs.notifySwiper && Object.keys(this.$refs.notifySwiper.$el.swiper).length && this.$refs.notifySwiper.$el.swiper.destroy(false, true);
          this.swiperVisible = false;
          return;
        }

        const sidebarWidth = this.needSidebar ? 56 : 0;
        const notifyPaddings = 16 * 2;
        const notifyIconWidth = 30;
        const closeIconWidth = val.closable ? 14 : 0;
        const contentWidth = Math.ceil(getStringWidth(val.content));
        const notifyWidth = document.body.offsetWidth - sidebarWidth - notifyPaddings - notifyIconWidth - closeIconWidth;

        this.swiperOptions = {
          autoplay: 3000,
          speed: 60000,
          loop: contentWidth > notifyWidth,
          spaceBetween: 200,
          observer: true,
          observeParents: true,
          width: contentWidth
        };

        // 确定好参数再展示，避免渲染重叠或错乱
        this.$nextTick(() => { this.swiperVisible = true; });
      },
      immediate: true,
      deep: true
    }
  },
  beforeRouteEnter(to, form, next) {
    next(async vm => {
      // 如果其他地方切换了语言，这里响应刷新菜单及页面
      let lang = db.get('LANGUAGE', 'zh');
      if (db.get('platform') === 'paas') {
        const paasLang = document.cookie.match(/lang=(\w+)/)?.[1]; // 形如zh_CN的格式
        Object.keys(LANGUAGE_MAP).forEach(key => {
          if (LANGUAGE_MAP[key] === paasLang) {
            lang = key;
          }
        });
      }
      if (vm.currentApp.code !== 'home' && vm.currentApp?.lang !== lang) {
        vm.lang = lang;
        db.save('LANGUAGE', lang);
        vm.$i18n.locale = lang;
        vm.$eventBus.$emit('updateAppRoute', vm.currentApp.code, () => { window.location.reload(); });
        return;
      }

      // 部分嵌入客户页面使用的场景，url上带token免登录
      const userToken = (to.query.userToken || '').replace('Bearer', '').trim();
      const configData = await vm.queryWebConfigData();
      if (configData?.VUE_APP_SUPPORT_LANG_SWITCH) {
        vm.supportLang = true;
      }
      if ((configData?.VUE_APP_TENANT_USER_TOKEN_ENABLE || process.env.VUE_APP_TENANT_USER_TOKEN_ENABLE) && userToken) {
        const appCode = Object.keys(APP_CONFIG_MAP).filter(code => code.toLowerCase().includes(to.path.split('/')[1]))[0] || '';
        const needUpdateApp = userToken !== vm.$store.state.account?.token || !vm.$store.state.account?.routes?.length || vm.currentApp.code !== appCode;
        needUpdateApp && vm.$eventBus.$emit('updateAppRoute', appCode, () => {
          vm.activeIndex = vm.getMenuIndexByPath(to.path);
        });
        return;
      }
    });
  },
  created() {
    // 全局设置监听，用于消息跳转应用页面前查询应用菜单
    this.$eventBus.$off('updateAppRoute');
    this.$eventBus.$on('updateAppRoute', async(code, callback) => {
      let appList = db.get('APPLIST', []);
      if (!appList.length) {
        const res = await baseApplicationApi.getMyAppCurTenant();
        const appVersion = await this.getVersion();
        if (res.data?.isSuccess) {
          appList = (res.data.data || []).map(item => ({
            ...item,
            isOpen: true,
            version: appVersion || APP_CONFIG_MAP[item.code].version
          }));
          this.$store.commit('account/setAppList', appList);
        }
      }
      const app = appList.filter(a => a.code === code)[0] || {};
      const hasApp = await this.onAppClick({
        ...app,
        isOpen: true
      }, false);
      hasApp && callback();
    });
    this.$eventBus.$off('updateNavbarIcons');
    this.$eventBus.$on('updateNavbarIcons', this.queryNavbarIcons);

    this.queryNavbarIcons();
    this.getFieldMappingList();

    // 非正式环境开启国际化切换
    // if (process.env.NODE_ENV !== 'production' || SUPPORT_LANG_TENANT.includes(this.tenant)) {
    //   this.supportLang = true;
    // }
  },
  mounted() {
    this.activeIndex = this.getMenuIndexByPath(this.$route.path, this.$route.query.url);
    this.getLogo();
    if (db.get('platform') === 'paas') {
      const defaultHome = `${window.location.protocol}//${window.location.hostname}:30068/`;
      this.homeCenter = process.env.VUE_APP_PAAS_HOME_URL || defaultHome;
      this.excludeNavMenu.push('personalCenter', 'tenantSwitch');
      this.connectPaas(this);
    } else {
      this.connect(this);
    }
  },
  methods: {
    /**
     * 查询图标数据
     */
    queryNavbarIcons() {
      this.loading = true;
      axiosAllSettled(Object.keys(this.iconMap).map(type => () => baseFileApi.queryCustomIcon({ type }))).then((iconResList) => {
        iconResList.forEach((res, index) => {
          if (res.isSuccess) {
            this.iconMap[index] = res.data || DEFAULT_ICON_MAP[index];
          }
        });
      }).finally(() => { this.loading = false; });
    },
    /**
     * 切换国际化处理
     */
    onChangeLang(lang) {
      // 国际化切换标记
      this.lang = lang;
      db.save('LANGUAGE', lang);
      this.$i18n.locale = lang;
      if (db.get('platform') === 'paas') {
        const host = window.location.hostname;
        const domain = host.includes('.com') ? host.split('.').slice(-2).join('.') : host;
        document.cookie = `lang=${LANGUAGE_MAP[lang]};domain=.${domain}`;
      }
      if (this.currentApp.code === 'home') {
        this.$router.go(0);
      } else {
        this.$eventBus.$emit('updateAppRoute', this.currentApp.code, () => { this.$router.go(0); });
      }
    },
    getFieldMappingList() {
      baseLangApi.getFieldMappingList({
        lang: this.lang
      }).then(({ data: { data }}) => {
        db.save('LANG_MAP', data || []);
      }).catch((e) => { console.log(e); }).finally(() => { this.isRouterViewShow = true; });
    },
    queryWebConfigData() {
      return baseCommonApi.getParameterByKey(process.env.VUE_APP_CONFIG_KEY, this.tenant).then(res => {
        if (res.data.isSuccess) {
          const configData = res.data.data?.value ? JSON.parse(res.data.data?.value) : '';
          let envData = process.env || {};
          if (typeof configData === 'object') {
            envData = Object.keys({ ...envData, ...configData }).reduce((pre, cur) => {
              pre[cur] = configData[cur] !== undefined ? configData[cur] : envData[cur];
              return pre;
            }, {});
          }
          this.$store.commit('common/setEnvConfig', envData);
          return Promise.resolve(envData);
        }
      });
    },
    reloadRouter() {
      this.isRouterViewShow = false;
      this.$nextTick(() => {
        this.isRouterViewShow = true;
      });
    },
    /**
     * 根据路由检索主菜单（本地路由配置、远端路由配置）
     * @param {String} path 当前路由，可能是主菜单路由或子路由
     * @param {String} frameUrl 如果是iframe展示，这里传展示的外部url
     */
    getMenuIndexByPath(path, frameUrl = '') {
      let activeIndex = -1;
      this.menuBreadcrumb = [];
      try {
        const decodedFrameUrl = decodeURIComponent(frameUrl); // iframe打开外部页面的形式，远端路由的path为外部url，component为/frame/index
        const navbarRouter = db.get('USER_ROUTER', []); // 远端配置的主菜单路由集
        // 匹配单菜单页面路由
        activeIndex = navbarRouter.findIndex(parent => {
          return parent.path === path || parent.path === decodedFrameUrl;
        });

        if (activeIndex < 0) {
          // 检索本地匹配的主菜单路由
          const routesCfg = this.$router.options.routes;
          let activeMenu = '';
          const matchRoute = routesCfg.filter(route => {
            if (route.path === path) {
              return true;
            }

            if (!route.children?.length) {
              return false;
            }

            return route.children.some(child => {
              if (child.path === path || `${route.path}/${child.path}` === path) {
                activeMenu = child.meta?.activeMenu || '';
                return true;
              }

              if (!child.children?.length) {
                return false;
              }

              return child.children.some(cchild => {
                if (cchild.path === path) {
                  activeMenu = cchild.meta?.activeMenu || '';
                  return true;
                }
                return false;
              });
            });
          });
          const matchParentPath = matchRoute[0]?.path ? `${matchRoute[0]?.path}/` : null; // 本地匹配的主菜单路由
          console.log('match local:', matchParentPath, activeMenu);

          // 匹配远端配置的主菜单路由，用于高亮选择
          activeIndex = navbarRouter.findIndex(parent => {
            // 主菜单匹配
            if (parent.path === matchParentPath ||
              parent.path === activeMenu ||
              (matchParentPath && (matchParentPath.startsWith(/(\/)$/.test(parent.path) ? parent.path : `${parent.path}/`)))
            ) {
              this.menuBreadcrumb.push({ title: parent.describe || parent.name, path: '' });
              const matches = parent.children?.length && parent.children.filter(child => child.path === path || child.path === activeMenu);
              matches?.length && this.menuBreadcrumb.push({ title: matches[0].describe || matches[0].name, path: matches[0].path });
              return true;
            }

            // 子孙菜单匹配当前页面路由，或包含主菜单路由从属
            return parent.children?.length && parent.children.some(child => {
              const childPath = child.path.split('?')[0];
              if (childPath === path || childPath === activeMenu || child.component === path) {
                this.menuBreadcrumb.push({ title: parent.describe || parent.name, path: '' });
                this.menuBreadcrumb.push({ title: child.describe || child.name, path: child.path });
                return true;
              }
              if (child.children?.length) {
                return child.children.some(cchild => {
                  const cchildPath = cchild.path.split('?')[0];
                  if (cchildPath === path || cchildPath === activeMenu || cchild.component === path) {
                    this.menuBreadcrumb.push({ title: parent.describe || parent.name, path: '' });
                    this.menuBreadcrumb.push({ title: child.describe || child.name, path: '' });
                    this.menuBreadcrumb.push({ title: cchild.describe || cchild.name, path: cchild.path });
                    return true;
                  }
                });
              }
            });
          });
        }

        // 兜底记录
        activeIndex > -1 && !this.menuBreadcrumb.length && this.menuBreadcrumb.push({
          title: navbarRouter[activeIndex].describe || navbarRouter[activeIndex].name,
          path: navbarRouter[activeIndex].path
        });
      } catch (e) {
        console.log(this.$t('更新导航栏菜单出错'), e);
      }

      if (activeIndex < 0) {
        activeIndex = parseInt(window.localStorage.getItem('activeIndex')) || '';
      }

      db.save('MENU_BREADCRUMB', this.menuBreadcrumb);

      return activeIndex.toString();
    },
    /**
     * 获取导航logo
     */
    getLogo() {
      baseTenantApi.getInfo().then(res => {
        if (res?.data?.isSuccess) {
          this.navLogo = res?.data?.data?.platformLogoUrl;
          this.tenantName = res?.data?.data?.name;
          this.$store.commit('account/setTenantName', this.tenantName);
        } else {
          this.$message.error(res.data.msg);
        }
      });
    },
    onMsgUpdate(msgData) {
      this.$store.commit('account/SET_MSG_DATA', msgData);
    },
    onSidebarSelect(path, route) {
      this.isNavbarClicked = true;
      // iframe打开外部url的情况下，path是外部url，component是/frame/index，反过来传参
      this.activeIndex = route?.component && route.component.includes('/frame/') ? this.getMenuIndexByPath(route.component, path) : this.getMenuIndexByPath(path, path);
    },
    /**
     * 关闭通知
     */
    handleCloseNotify() {
      this.notify.visible = false;
      this.$store.commit('account/setNotify', this.notify);
    },
    handleClickOutside() {
      this.$store.commit('setting/closeSidebar', { withoutAnimation: false });
    },
    /**
     * 关闭料单抽屉
     */
    handleMaterialDrawerClosed() {
      this.materialDrawer.visible = false;
    }
  }
};
</script>

<style lang="scss">
.hasNotify{
  .fixed-top{
    top: var(--top) !important;
  }
}
// fix css style bug in open el-dialog
// 原框架自带的处理，不知用意，但是加上会影响顶部导航栏宽度，先注释
// .el-popup-parent--hidden {
//   .fixed-header {
//     padding-right: 15px;
//   }
// }
</style>

<style lang="scss" scoped>
@import "~@/styles/mixin.scss";
.notify {
  display: flex;
  width: calc(100% - #{$sidebar-width-small});
  background-color: #FFFBE6;
  border: 1px solid #FFE58F;
  position: fixed;
  font-size: 14px;
  padding: 0 16px;
  left: $sidebar-width-small;
  .el-icon-warning {
    color: #FAAD14;
    padding-right: 16px;
    line-height: inherit;
  }
  .el-icon-close {
    line-height: inherit;
    margin-left: auto;
    cursor: pointer;
    &:hover{
      color: #FAAD14
    }
  }
}

.hideSidebar {
  .notify {
    width: 100%;
    left: 0px;
  }
}

::v-deep{
  .swiper-container{
    margin-left: 0;
    margin-right: 0;
  }
  .swiper-wrapper{
    transition-timing-function: linear;
  }
  .swiper-slide{
    white-space: nowrap;
  }
}

.app-wrapper {
  @include clearfix;
  position: relative;
  height: 100%;
  width: 100%;

  .app-sidebar {
    height: calc(100% - #{$navbar-height});
    margin-top: $navbar-height;
  }

  &.mobile.openSidebar {
    position: fixed;
    top: 0;
  }

  .app-main {
    min-height: calc(100vh - #{$navbar-height});
    width: 100%;
    position: relative;
    overflow: hidden;
    background: white;
    font-size: .9rem;
  }

  .fixed-header+.app-main {
    padding-top: $navbar-height;
    height: 100vh;
    overflow: auto;
  }

  .hasTagsView {
    .app-main {
      /* 90 = navbar + tags-view = 56 + 34 */
      min-height: calc(100vh - 90px);
    }
  }
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

.fixed-header {
  position: fixed;
  top: 0;
  right: 0;
  z-index: $nav-side-fixed-index;
  width: 100%;
  transition: width 0.28s;
}

.unlockNotify {
  .txt {
    word-break: break-all;
  }

  .btn {
    float: right;
    .check-btn {
      margin-right: 0;
      color: #ffffff;
      background: #ff6900;
      border-color: #ff6900;

      &:hover {
        background: #ff8733;
        border-color: #ff8733;
      }
    }

    .hidden-link {
      display: none;
    }

    .show-link {
      color: #409eff;

      &:after {
        border-bottom: 1px solid #409eff;
      }
    }
  }
}

.mat-dialog {
  .mat-title {
    font-weight: 700;
    margin-bottom: 10px;
  }
}
</style>
